import React, { useState, useRef, useCallback } from 'react';
import { EvalFormData } from '../types/evalform';
import PreviewSectionCard from './PreviewSectionCard';
import '../styles/components/EvaluationPanel.css';
import NavigationTabs, { TabItem } from './common/NavigationTabs';

interface FormPreviewPanelProps {
  formData: EvalFormData;
  moments?: MomentItem[];
  operations?: MomentOperationInterface[];
}

interface MatchPosition {
  element: Element;
  top: number;
}

interface MomentOperationInterface {
  id: number;
  name: string;
  description: string;
}

interface MomentItem {
  id: number;
  name: string;
  description?: string;
  category_id?: number;
}


const FormPreviewPanel: React.FC<FormPreviewPanelProps> = ({ formData, moments = [], operations = [] }) => {
  // Search related state
  const [searchText, setSearchText] = useState('');
  const [matchPositions, setMatchPositions] = useState<MatchPosition[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);

  // Enhanced search function
  const handleSearch = useCallback((text: string) => {
    setSearchText(text);
    
    if (text && formData) {
      // Set a small timeout to let React render the highlights first
      setTimeout(() => {
        const highlights = panelRef.current?.querySelectorAll('.search-highlight') || [];
        
        if (highlights.length > 0) {
          const positions = Array.from(highlights).map(el => {
            const rect = el.getBoundingClientRect();
            return {
              element: el,
              top: rect.top
            };
          });
          
          setMatchPositions(positions);
          setCurrentMatchIndex(0);
          
          // Scroll to first match
          if (positions.length > 0 && positions[0].element) {
            positions[0].element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } else {
          setMatchPositions([]);
          setCurrentMatchIndex(-1);
        }
      }, 200);
    } else {
      setMatchPositions([]);
      setCurrentMatchIndex(-1);
    }
  }, [formData]);

  // Navigate to next match
  const goToNextMatch = useCallback(() => {
    if (matchPositions.length > 0) {
      const nextIndex = (currentMatchIndex + 1) % matchPositions.length;
      setCurrentMatchIndex(nextIndex);
      scrollToMatch(nextIndex);
    }
  }, [matchPositions, currentMatchIndex]);

  // Navigate to previous match
  const goToPreviousMatch = useCallback(() => {
    if (matchPositions.length > 0) {
      const prevIndex = (currentMatchIndex - 1 + matchPositions.length) % matchPositions.length;
      setCurrentMatchIndex(prevIndex);
      scrollToMatch(prevIndex);
    }
  }, [matchPositions, currentMatchIndex]);

  // Scroll to a specific match
  const scrollToMatch = (index: number) => {
    if (matchPositions[index]?.element) {
      matchPositions[index].element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  };

  // Define tabs for NavigationTabs
  const tabItems: TabItem[] = [
    {
      id: 'preview',
      label: formData?.name || "Form Preview",
      icon: 'assessment',
      searchable: true
    }
  ];

  return (
    <div className="evaluation-panel">
      <NavigationTabs
        tabs={tabItems}
        activeTab="preview"
        onTabChange={() => {}}
        searchValue={searchText}
        onSearch={handleSearch}
        onNavigateNext={goToNextMatch}
        onNavigatePrevious={goToPreviousMatch}
        searchStats={
          matchPositions.length > 0
            ? {
                currentMatch: currentMatchIndex + 1,
                totalMatches: matchPositions.length
              }
            : undefined
        }
      />
     
      <div className="sections-container" ref={panelRef}>
        {formData.sections.map((section, sectionIndex) => (
          <PreviewSectionCard
            key={section.id || sectionIndex}
            section={section}
            sectionIndex={sectionIndex}
            searchText={searchText}
            moments={moments}
            operations={operations}
          />
        ))}
      </div>
    </div>
  );
};

export default FormPreviewPanel;

